import { useQuery } from "@tanstack/react-query";
import { validate as isUuid } from "uuid";

import { hasDataArray } from "@/api/connection.types";
import { FilesQueryKeys } from "@/Features/Files/files.query";

import { AssetAttachmentsAPI } from "./asset-attachments.api";
import type { AssetAttachment } from "./asset-attachments.types";

/**
 * Every report of one validation (ENG-1673), newest first. A single page: one
 * validation never has more than a handful. Keyed under FilesQueryKeys, so the
 * Attachments tab's upload and delete refresh it too. A connection failure
 * throws so the section can render its own error line. `refetchOnMount:
 * "always"` re-signs the links on every open.
 */
export const useValidationReports = (
  asset_uuid: string,
  validation_uuid: string
) => {
  const query = { asset_uuid, validation_uuid, page_size: 1000 };
  return useQuery({
    queryKey: FilesQueryKeys.list(query),
    queryFn: async (): Promise<AssetAttachment[]> => {
      // The section renders its own error line, so no toast.
      const result: unknown = await AssetAttachmentsAPI.get(asset_uuid, query, {
        skipErrorToast: true,
      });
      if (!hasDataArray(result)) {
        throw new Error(`Failed to load reports for ${validation_uuid}`);
      }
      return result.data as AssetAttachment[];
    },
    enabled: isUuid(asset_uuid) && isUuid(validation_uuid),
    retry: false,
    refetchOnMount: "always",
  });
};
